import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { submitQuizFn } from "@/lib/quiz.functions";
import { useAuth } from "@/auth/AuthProvider";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Loader2, CheckCircle2, XCircle, Trophy, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/student/quiz/$quizId")({
  component: QuizPlayer,
});

interface Question {
  id: string;
  question: string;
  question_type: "multiple_choice" | "true_false";
  options: string[];
  points: number;
}
interface QuizResult {
  score: number;
  max: number;
  pct: number;
  passed: boolean;
  pointsReward: number;
  gradedAnswers: Record<
    string,
    { correct: boolean; explanation: string | null; correctAnswer: string }
  >;
}
interface QuizMeta {
  id: string;
  title: string;
  description: string | null;
  pass_score: number;
  points_reward: number;
}

function QuizPlayer() {
  const { quizId } = Route.useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [quiz, setQuiz] = useState<QuizMeta | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState<QuizResult | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      const [q, qq] = await Promise.all([
        supabase
          .from("quizzes")
          .select("id,title,description,pass_score,points_reward")
          .eq("id", quizId)
          .maybeSingle(),
        supabase
          .from("quiz_questions")
          .select("id,question,question_type,options,points")
          .eq("quiz_id", quizId)
          .order("sort_order"),
      ]);
      setQuiz(q.data);
      setQuestions(
        ((qq.data ?? []) as unknown as Question[]).map((row) => ({
          ...row,
          options: Array.isArray(row.options)
            ? row.options
            : typeof row.options === "string"
              ? JSON.parse(row.options)
              : [],
        })),
      );
      setLoading(false);
    })();
  }, [quizId]);

  const select = (questionId: string, value: string) =>
    setAnswers((a) => ({ ...a, [questionId]: value }));

  const submit = async () => {
    if (!user || !quiz) return;
    setSubmitting(true);
    try {
      const res = await submitQuizFn({ data: { quizId: quiz.id, answers } });
      setResult(res);
      setSubmitted(true);
      if (res.passed) {
        toast.success(`🎉 نجحت! +${res.pointsReward} نقطة`);
      } else {
        toast.error("للأسف مكفتش — جرب تاني!");
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to submit quiz");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  if (!quiz)
    return (
      <div className="container mx-auto px-4 py-12">
        <p>Quiz not found.</p>
      </div>
    );
  if (questions.length === 0)
    return (
      <div className="container mx-auto px-4 py-12">
        <p>لا توجد أسئلة بعد.</p>
      </div>
    );

  if (submitted && result) {
    return (
      <div className="container mx-auto px-4 py-12 max-w-2xl">
        <Card className="rounded-3xl border-0 shadow-soft text-center bg-gradient-to-br from-primary/10 to-accent/10">
          <CardContent className="p-10">
            <div className="text-7xl mb-4">{result.passed ? "🎉" : "💪"}</div>
            <h1 className="text-3xl font-extrabold mb-2">
              {result.passed ? "ممتاز!" : "حاول تاني"}
            </h1>
            <p className="text-muted-foreground mb-6">
              حصلت على {result.score} من {result.max} نقطة
            </p>
            <div className="text-6xl font-extrabold text-primary mb-2">{result.pct}%</div>
            <Progress value={result.pct} className="h-3 mb-6" />
            {result.passed && (
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-300 font-bold mb-6">
                <Trophy className="h-4 w-4" /> +{result.pointsReward} Cubs Points
              </div>
            )}
            <div className="flex gap-3 justify-center">
              <Button
                onClick={() => navigate({ to: "/student" })}
                className="rounded-full bg-gradient-primary text-primary-foreground border-0"
              >
                رجوع للوحة
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setSubmitted(false);
                  setIdx(0);
                  setAnswers({});
                  setResult(null);
                }}
                className="rounded-full"
              >
                إعادة المحاولة
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="mt-8 space-y-3">
          <h2 className="font-bold text-lg">مراجعة الإجابات</h2>
          {questions.map((q, i) => {
            const graded = result.gradedAnswers[q.id];
            const correct = graded?.correct;
            return (
              <Card
                key={q.id}
                className={`rounded-2xl border-0 shadow-soft ${correct ? "bg-emerald-500/5" : "bg-rose-500/5"}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    {correct ? (
                      <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5 shrink-0" />
                    ) : (
                      <XCircle className="h-5 w-5 text-rose-500 mt-0.5 shrink-0" />
                    )}
                    <div className="flex-1">
                      <div className="font-medium">
                        {i + 1}. {q.question}
                      </div>
                      <div className="text-sm mt-2">
                        إجابتك:{" "}
                        <span
                          className={
                            correct ? "text-emerald-600 font-bold" : "text-rose-600 font-bold"
                          }
                        >
                          {answers[q.id] || "—"}
                        </span>
                      </div>
                      {!correct && (
                        <div className="text-sm text-emerald-600 font-bold">
                          الصح: {graded?.correctAnswer}
                        </div>
                      )}
                      {graded?.explanation && (
                        <p className="text-xs text-muted-foreground mt-2">
                          💡 {graded.explanation}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  const q = questions[idx];
  const opts = q.question_type === "true_false" ? ["صح", "غلط"] : q.options;
  const allAnswered = questions.every((qq) => answers[qq.id]);

  return (
    <div className="container mx-auto px-4 py-10 max-w-2xl">
      <div className="mb-6">
        <Link to="/student" className="text-sm text-muted-foreground hover:text-primary">
          ← رجوع
        </Link>
        <h1 className="text-2xl font-extrabold mt-2">{quiz.title}</h1>
        {quiz.description && (
          <p className="text-muted-foreground text-sm mt-1">{quiz.description}</p>
        )}
      </div>

      <div className="mb-4 flex items-center justify-between text-sm">
        <span className="font-bold">
          سؤال {idx + 1} من {questions.length}
        </span>
        <span className="text-muted-foreground">{q.points} نقطة</span>
      </div>
      <Progress value={((idx + 1) / questions.length) * 100} className="h-2 mb-6" />

      <Card className="rounded-3xl border-0 shadow-soft mb-6">
        <CardContent className="p-6">
          <h2 className="text-xl font-bold mb-6">{q.question}</h2>
          <div className="space-y-2">
            {opts.map((o, i) => {
              const selected = answers[q.id] === o;
              return (
                <button
                  key={i}
                  onClick={() => select(q.id, o)}
                  className={`w-full text-start p-4 rounded-2xl border-2 transition font-medium ${selected ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"}`}
                >
                  <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-muted text-xs font-bold me-3">
                    {String.fromCharCode(65 + i)}
                  </span>
                  {o}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-3 justify-between">
        <Button
          variant="outline"
          disabled={idx === 0}
          onClick={() => setIdx((i) => i - 1)}
          className="rounded-full"
        >
          السابق
        </Button>
        {idx < questions.length - 1 ? (
          <Button
            disabled={!answers[q.id]}
            onClick={() => setIdx((i) => i + 1)}
            className="rounded-full bg-gradient-primary text-primary-foreground border-0"
          >
            التالي <ArrowRight className="h-4 w-4 ms-1" />
          </Button>
        ) : (
          <Button
            disabled={!allAnswered || submitting}
            onClick={submit}
            className="rounded-full bg-gradient-primary text-primary-foreground border-0"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} سلّم الإجابات
          </Button>
        )}
      </div>
    </div>
  );
}
